import { useEffect, useMemo, useState } from 'react';
import {
  Check,
  Plus,
  Trash2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  GripVertical,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import type { Campaign, Location, Scene } from '../../../shared/types';
import { useCollection } from '../lib/hooks';
import { updateCampaign, startCampaign, errorText } from '../lib/api';
import { driveThumbnailUrl } from '../lib/drive';
import ThumbPreview from './ThumbPreview';
import ImageLibrary from './ImageLibrary';
import { confirmDialog } from './ConfirmDialog';

/**
 * Script review: edit the scenes, then approve to start rendering.
 *
 * Approving used to write `Video Status = START` into a cell and hope somebody
 * had an n8n execution running to notice. Now it enqueues the render directly.
 */

/** Scene speech budget: an 8s clip fits ~18 comfortably spoken words. */
const WORDS_OK = 18;
const WORDS_TIGHT = 25;

function wordCount(text: string | undefined): number {
  return (text ?? '').trim().split(/\s+/).filter(Boolean).length;
}

/**
 * One custom-location row: a Drive link with live accessibility validation.
 * The check is honest — it loads the actual thumbnail, so "valid" means the
 * image really is publicly reachable, which is what kie.ai needs too.
 */
function CustomLocationRow({
  index,
  url,
  onChange,
  onRemove,
}: {
  index: number;
  url: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}) {
  const [status, setStatus] = useState<'idle' | 'testing' | 'valid' | 'invalid'>('idle');
  const [testUrl, setTestUrl] = useState('');

  useEffect(() => {
    const trimmed = url.trim();
    if (!trimmed) {
      setStatus('idle');
      setTestUrl('');
      return;
    }
    if (!trimmed.includes('drive.google.com')) {
      setStatus('invalid');
      setTestUrl('');
      return;
    }
    setTestUrl(driveThumbnailUrl(trimmed, 'w200'));
    setStatus('testing');
  }, [url]);

  const tag = `cust_loc_${String(index + 1).padStart(2, '0')}`;

  return (
    <div className="p-3 rounded-xl bg-natural-bg/60 border border-natural-border space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] font-semibold text-natural-sage bg-natural-sage/15 px-2 py-0.5 rounded-full">
            {tag}
          </span>
          {status === 'valid' && (
            <span className="zen-pill bg-emerald-50 text-emerald-700">
              <CheckCircle2 size={10} />
              Public link
            </span>
          )}
          {status === 'invalid' && (
            <span className="zen-pill bg-red-50 text-red-600">
              <AlertTriangle size={10} />
              Private or invalid
            </span>
          )}
          {status === 'testing' && (
            <span className="zen-pill bg-amber-50 text-amber-700">
              <Loader2 size={10} className="animate-spin" />
              Checking…
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-1.5 rounded-lg text-natural-text/35 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
          title="Remove custom location"
        >
          <Trash2 size={13} />
        </button>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste a Google Drive image link (https://drive.google.com/file/d/…/view)"
          className="zen-input text-xs font-mono"
        />
        {testUrl && (
          <img
            src={testUrl}
            alt={tag}
            referrerPolicy="no-referrer"
            onLoad={() => setStatus('valid')}
            onError={() => setStatus('invalid')}
            className="w-9 h-9 rounded-lg object-cover border border-natural-border bg-stone-100 shrink-0"
          />
        )}
      </div>

      {status === 'invalid' && url.trim().includes('drive.google.com') && (
        <p className="text-[11px] text-red-600 flex items-center gap-1">
          <AlertTriangle size={11} className="shrink-0" />
          This link won't load. Set the file's sharing to "Anyone with the link can view".
        </p>
      )}
    </div>
  );
}

export default function ScriptEditor({ campaign }: { campaign: Campaign }) {
  const [scenes, setScenes] = useState<Scene[]>(campaign.scenes ?? []);
  const [customLocations, setCustomLocations] = useState<string[]>(
    campaign.custom_locations ?? []
  );
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<'save' | 'approve' | 'regenerate' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [baseline, setBaseline] = useState(campaign.updated);
  // Which scene the image library is open for, if any.
  const [libraryFor, setLibraryFor] = useState<number | null>(null);

  // This influencer's rooms plus the global ones — the dropdown options. A tag
  // is a contract with the render stage, so free-typing it (v2's first pass)
  // meant a typo silently fell back to the first location and rendered the
  // wrong room.
  const locations = useCollection<Location>('locations', {
    filter: campaign.influencer
      ? `influencer = "${campaign.influencer}" || is_global = true`
      : 'is_global = true',
    sort: 'sort',
  });

  const locationOptions = useMemo(() => {
    const fromDb = locations.items.map((l) => ({
      tag: l.tag,
      label: l.name || l.tag,
      url: l.drive_url,
    }));
    const custom = customLocations.map((url, i) => ({
      tag: `cust_loc_${String(i + 1).padStart(2, '0')}`,
      label: `Custom location ${i + 1}`,
      url,
    }));
    return [...fromDb, ...custom];
  }, [locations.items, customLocations]);

  // Accept changes arriving over the subscription unless there are local edits
  // — otherwise a background write would silently discard what's being typed.
  useEffect(() => {
    if (!dirty) {
      setScenes(campaign.scenes ?? []);
      setCustomLocations(campaign.custom_locations ?? []);
      setBaseline(campaign.updated);
    }
  }, [campaign.scenes, campaign.custom_locations, campaign.updated, dirty]);

  const working = campaign.stage === 'script_generating';

  if (working) {
    return (
      <div className="zen-card p-10 text-center space-y-4">
        <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-natural-bg">
          <span className="text-3xl">✍️</span>
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-natural-sage/40 animate-spin-slower" />
        </div>
        <p className="font-serif text-lg font-medium">
          {campaign.stage_message || 'Writing the script…'}
        </p>
      </div>
    );
  }

  if (!scenes.length) {
    return (
      <div className="zen-card p-10 text-center text-xs text-natural-text/50">
        No scenes yet. The script is still being prepared.
      </div>
    );
  }

  const update = (index: number, patch: Partial<Scene>) => {
    setScenes((current) => current.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    setDirty(true);
  };

  const remove = (index: number) => {
    setScenes((current) =>
      current.filter((_, i) => i !== index).map((s, i) => ({ ...s, sceneIndex: i }))
    );
    setDirty(true);
  };

  const add = () => {
    setScenes((current) => [
      ...current,
      {
        sceneIndex: current.length,
        action: '',
        speech: '',
        location_tag: current[0]?.location_tag ?? locationOptions[0]?.tag ?? '',
        holding_product: 'NO',
      },
    ]);
    setDirty(true);
  };

  const updateCustomLocation = (index: number, value: string) => {
    setCustomLocations((current) => current.map((u, i) => (i === index ? value : u)));
    setDirty(true);
  };

  const removeCustomLocation = async (index: number) => {
    const tag = `cust_loc_${String(index + 1).padStart(2, '0')}`;
    const used = scenes.some((s) => s.location_tag === tag);
    if (
      used &&
      !(await confirmDialog({
        title: 'Location still in use',
        message: `A scene still uses ${tag}. Remove it anyway? That scene will need a new location.`,
        confirmLabel: 'Remove',
        danger: true,
      }))
    ) {
      return;
    }
    // Later custom tags shift down by one, so repoint any scene that used them.
    setScenes((current) =>
      current.map((s) => {
        const m = s.location_tag?.match(/^cust_loc_(\d+)$/);
        if (!m) return s;
        const n = Number(m[1]);
        if (n - 1 === index) return { ...s, location_tag: '' };
        if (n - 1 > index) return { ...s, location_tag: `cust_loc_${String(n - 1).padStart(2, '0')}` };
        return s;
      })
    );
    setCustomLocations((current) => current.filter((_, i) => i !== index));
    setDirty(true);
  };

  const persist = async () => {
    const normalised = scenes.map((s, i) => ({ ...s, sceneIndex: i }));
    const updated = await updateCampaign(
      campaign.id,
      { scenes: normalised, custom_locations: customLocations.map((u) => u.trim()).filter(Boolean) },
      baseline
    );
    setBaseline(updated.updated);
    setDirty(false);
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      await persist();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    // The one button that spends money gets a confirmation, like v1 had —
    // and it names the problems that would otherwise surface mid-render.
    const pinnedCount = scenes.filter((s) => s.pinned_image_url).length;
    const warnings: string[] = [];
    if (overLong > 0) warnings.push(`${overLong} scene(s) are over ${WORDS_TIGHT} words`);
    if (missingLocation > 0) {
      warnings.push(`${missingLocation} scene(s) have no location and will use the first one`);
    }
    const message =
      `This will generate ${scenes.length - pinnedCount} image(s).` +
      (pinnedCount ? `\n${pinnedCount} scene(s) reuse a pinned image — free.` : '') +
      (warnings.length ? `\n\nHeads up:\n• ${warnings.join('\n• ')}` : '');
    if (
      !(await confirmDialog({
        title: 'Approve and render images?',
        message,
        confirmLabel: 'Render images',
      }))
    ) {
      return;
    }

    setBusy('approve');
    setError(null);
    try {
      if (dirty) await persist();
      await startCampaign(campaign.id, 'render_images');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async () => {
    if (
      !(await confirmDialog({
        title: campaign.mode === 'custom' ? 'Re-split the script?' : 'Regenerate the script?',
        message:
          campaign.mode === 'custom'
            ? 'The pasted script will be split into scenes again. Your current scene edits will be replaced.'
            : 'A new script will be written with AI. Your current scenes and edits will be replaced.',
        confirmLabel: 'Regenerate',
        danger: true,
      }))
    ) {
      return;
    }
    setBusy('regenerate');
    setError(null);
    try {
      // Save first: custom locations aren't part of the script and must not
      // die with it — only the scenes get replaced by the regeneration.
      if (dirty) await persist();
      await startCampaign(campaign.id, 'generate_script');
      setDirty(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const totalWords = scenes.reduce((n, s) => n + wordCount(s.speech), 0);
  const overLong = scenes.filter((s) => wordCount(s.speech) > WORDS_TIGHT).length;
  // A pinned scene needs no location — it skips generation entirely.
  const missingLocation = scenes.filter((s) => !s.location_tag && !s.pinned_image_url).length;

  return (
    <div className="zen-card p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg font-medium">Script</h3>
          <p className="text-xs text-natural-text/50 mt-0.5">
            {scenes.length} scenes · {totalWords} words · roughly{' '}
            {Math.round(scenes.length * 7.8)}s of video
          </p>
        </div>
        {dirty && <span className="zen-pill bg-amber-50 text-amber-700">Unsaved changes</span>}
      </div>

      {/* Above the scenes on purpose: add the location links first, then they
          are already in every scene's dropdown while writing the script. */}
      <details className="group" open={customLocations.length > 0}>
        <summary className="text-xs text-natural-text/55 cursor-pointer hover:text-natural-sage select-none">
          Custom locations ({customLocations.length})
        </summary>
        <div className="mt-3 space-y-2">
          {customLocations.map((url, i) => (
            <CustomLocationRow
              key={i}
              index={i}
              url={url}
              onChange={(value) => updateCustomLocation(i, value)}
              onRemove={() => removeCustomLocation(i)}
            />
          ))}
          <button
            type="button"
            onClick={() => {
              setCustomLocations((current) => [...current, '']);
              setDirty(true);
            }}
            className="zen-btn-ghost w-full py-2 text-xs"
          >
            <Plus size={13} />
            Add a custom location
          </button>
          <p className="text-[11px] text-natural-text/45">
            Custom locations become extra options in each scene's Location dropdown. Saved with
            the script.
          </p>
        </div>
      </details>

      <div className="space-y-3">
        {scenes.map((scene, index) => {
          const words = wordCount(scene.speech);
          const selectedLocation = locationOptions.find((o) => o.tag === scene.location_tag);
          const unknownTag =
            Boolean(scene.location_tag) && !selectedLocation && !locations.loading;

          return (
            <div key={index} className="border border-natural-border rounded-2xl p-4 bg-natural-bg/40">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <GripVertical size={13} className="text-natural-text/25" />
                  <span className="font-mono text-[10px] text-natural-text/45">
                    SCENE {index + 1}
                  </span>
                </div>
                <button
                  onClick={() => remove(index)}
                  className="p-1.5 rounded-lg text-natural-text/35 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  title="Remove scene"
                >
                  <Trash2 size={13} />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between">
                    <label className="zen-label">Spoken line</label>
                    <span
                      className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full ${
                        words > WORDS_TIGHT
                          ? 'bg-red-50 text-red-600'
                          : words > WORDS_OK
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {words} words
                    </span>
                  </div>
                  <textarea
                    value={scene.speech}
                    onChange={(e) => update(index, { speech: e.target.value })}
                    rows={2}
                    className="zen-input text-sm resize-y"
                  />
                  {words > WORDS_TIGHT && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1">
                      <AlertTriangle size={11} className="shrink-0" />
                      Too long for one {Math.round(7.8)}s clip — the voice will rush or get cut
                      off. Aim for {WORDS_OK} words or fewer.
                    </p>
                  )}
                </div>

                <div>
                  <label className="zen-label">Action / shot description</label>
                  <textarea
                    value={scene.action}
                    onChange={(e) => update(index, { action: e.target.value })}
                    rows={2}
                    className="zen-input text-xs resize-y"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    {scene.pinned_image_url ? (
                      <>
                        <label className="zen-label">Pinned image</label>
                        <div className="flex items-center gap-2">
                          <ThumbPreview
                            url={scene.pinned_image_url}
                            alt={`Scene ${index + 1} pinned image`}
                            className="w-9 h-9"
                          />
                          <span className="zen-pill bg-natural-sage/15 text-natural-sage">
                            Reusing — no generation cost
                          </span>
                          <button
                            type="button"
                            onClick={() => update(index, { pinned_image_url: '' })}
                            className="text-[11px] text-natural-text/50 hover:text-red-500 cursor-pointer underline underline-offset-2"
                          >
                            Unpin
                          </button>
                        </div>
                        <p className="text-[11px] text-natural-text/45 mt-1">
                          This scene skips image generation and uses the pinned image as-is.
                        </p>
                      </>
                    ) : (
                      <>
                        <label className="zen-label">Location</label>
                        <div className="flex items-center gap-2">
                          <select
                            value={scene.location_tag ?? ''}
                            onChange={(e) => update(index, { location_tag: e.target.value })}
                            className="zen-input text-xs font-mono"
                          >
                            <option value="">Choose…</option>
                            {locationOptions.map((o) => (
                              <option key={o.tag} value={o.tag}>
                                {o.label}
                              </option>
                            ))}
                            {unknownTag && (
                              <option value={scene.location_tag}>
                                {scene.location_tag} (unknown)
                              </option>
                            )}
                          </select>
                          {selectedLocation?.url && (
                            <ThumbPreview
                              url={selectedLocation.url}
                              alt={selectedLocation.label}
                              className="w-9 h-9"
                            />
                          )}
                        </div>
                        {unknownTag && (
                          <p className="text-[11px] text-amber-700 mt-1">
                            This tag doesn't match any location — the render would fall back to
                            the first one. Pick from the list.
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => setLibraryFor(index)}
                          className="text-[11px] text-natural-sage hover:underline underline-offset-2 mt-1 cursor-pointer"
                        >
                          …or reuse an existing image (free)
                        </button>
                      </>
                    )}
                  </div>
                  <div>
                    <label className="zen-label">Holding the product</label>
                    <div className="flex gap-1 p-1 bg-stone-100/80 rounded-xl">
                      {(['YES', 'NO'] as const).map((value) => (
                        <button
                          key={value}
                          onClick={() => update(index, { holding_product: value })}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                            (scene.holding_product ?? 'NO') === value
                              ? 'bg-white shadow-xs'
                              : 'text-natural-text/60'
                          }`}
                        >
                          {value === 'YES' ? 'Yes' : 'No'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={add} className="zen-btn-ghost w-full">
        <Plus size={14} />
        Add a scene
      </button>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {overLong > 0 && (
        <p className="text-[11px] text-amber-700">
          {overLong} scene{overLong > 1 ? 's are' : ' is'} over {WORDS_TIGHT} words — consider
          trimming before rendering.
        </p>
      )}

      {missingLocation > 0 && (
        <p className="text-[11px] text-amber-700">
          {missingLocation} scene{missingLocation > 1 ? 's have' : ' has'} no location selected —
          the render would fall back to the influencer's first room.
        </p>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={save} disabled={!dirty || busy !== null} className="zen-btn-ghost">
          {busy === 'save' ? 'Saving…' : 'Save draft'}
        </button>
        <button
          onClick={regenerate}
          disabled={busy !== null}
          className="zen-btn-ghost"
          title={
            campaign.mode === 'custom'
              ? 'Re-split the pasted script into scenes'
              : 'Throw this script away and write a new one'
          }
        >
          <RefreshCw size={14} />
          {busy === 'regenerate' ? 'Starting…' : 'Regenerate'}
        </button>
        <button onClick={approve} disabled={busy !== null} className="zen-btn-primary flex-1">
          <Check size={15} />
          {busy === 'approve' ? 'Starting…' : 'Approve and render images'}
        </button>
      </div>

      {libraryFor !== null && (
        <ImageLibrary
          influencerId={campaign.influencer}
          productId={campaign.product}
          locationTag={scenes[libraryFor]?.location_tag}
          holdingProduct={scenes[libraryFor]?.holding_product}
          onPick={(pick) => {
            update(libraryFor, { pinned_image_url: pick.url });
            setLibraryFor(null);
          }}
          onClose={() => setLibraryFor(null)}
        />
      )}
    </div>
  );
}
